import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import SecureLS from "secure-ls";
import Swal from "sweetalert2";
import ReceiptHero from "./ReceiptHero";
import ReceiptShiftConfig from "./ReceiptShiftConfig";

const ls = new SecureLS({ encodingType: "aes" });

const dayList = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ReceiptGrossSales = () => {
  const [goals, setGoals] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [useNet, setUseNet] = useState(false);

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const token = ls.get("authToken");
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/gross-sales-goal`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const fetched = {};
        for (const entry of res.data.goals || []) {
          fetched[entry.day] = entry.amount.toString();
        }
        setGoals(fetched);
        setUseNet(res.data.useNet ?? false);
      } catch (err) {
        console.error("Error fetching gross sales goals:", err);
        Swal.fire("Error", "Failed to fetch gross sales goals.", "error");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchGoals();
  }, []);

  const handleChange = (day, value) => {
    if (/^\d*$/.test(value)) {
      setGoals((prev) => ({
        ...prev,
        [day]: value,
      }));
    }
  };

  const handleSave = async () => {
    const token = ls.get("authToken");
    try {
      setLoading(true);
      const payload = dayList.map((day) => ({
        day,
        amount: parseInt(goals[day] || "0", 10),
      }));

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/edit-gross-sales-goal`,
        { goals: payload },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      Swal.fire("Success", "Gross sales goals updated.", "success");
    } catch (err) {
      console.error("Error saving goals:", err);
      Swal.fire("Error", "Failed to save gross sales goals.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <ReceiptHero />

      <Typography variant="h6" sx={{ mt: 4, mb: 2, textAlign: "center" }}>
        Daily Sales Goals (₱)
      </Typography>

      {initialLoading ? (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <ReceiptShiftConfig />

          <Box
            sx={{
              maxWidth: 400,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Tooltip
              title={
                useNet
                  ? "Switch to Gross Sales Goals"
                  : "Switch to Net Sales Goals"
              }
              arrow
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={useNet}
                    onChange={async () => {
                      const token = ls.get("authToken");
                      try {
                        const nextValue = !useNet;

                        Swal.fire({
                          title: "Switching...",
                          text: `Changing to ${
                            nextValue ? "Net" : "Gross"
                          } Sales Goals`,
                          allowOutsideClick: false,
                          didOpen: () => {
                            Swal.showLoading();
                          },
                        });

                        await axios.post(
                          `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/switch-gross-net-sales-goal`,
                          { useNet: nextValue },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );

                        setUseNet(nextValue);

                        Swal.fire(
                          "Success",
                          `Switched to ${
                            nextValue ? "Net" : "Gross"
                          } Sales Goals.`,
                          "success"
                        );
                      } catch (err) {
                        console.error("Error switching sales goal basis:", err);
                        Swal.fire(
                          "Error",
                          "Failed to switch sales goal basis.",
                          "error"
                        );
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  useNet ? "Using Net Sales Goals" : "Using Gross Sales Goals"
                }
                labelPlacement="start"
                sx={{ justifyContent: "space-between", mx: 1 }}
              />
            </Tooltip>

            {dayList.map((day) => (
              <TextField
                key={day}
                label={day}
                type="text"
                value={goals[day] || ""}
                onChange={(e) => handleChange(day, e.target.value)}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            ))}

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ReceiptGrossSales;
